import { Button } from "@mui/material"
import React, { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useMutation } from "react-query"
import Swal from "sweetalert2"
import { LocationState } from "../../api/models/cvskill"
import cvskillService from "../../api/services/cvskillService"

interface AtoutType {
	id: string
	name: string
}

const atouts: AtoutType[] = [
	{ id: "1", name: "Adaptation" },
	{ id: "2", name: "Organisation" },
	{ id: "3", name: "Rigueur" },
	{ id: "4", name: "Dynamisme" },
	{ id: "5", name: "Ténacité" },
	{ id: "6", name: "Travail en équipe" },
	{ id: "7", name: "Négociation" },
	{ id: "8", name: "Respect des règles" },
	{ id: "9", name: "Sociabilité" },
	{ id: "10", name: "Maîtrise émotionnelle" },
	{ id: "11", name: "Autonomie" },
	{ id: "12", name: "Ambition" },
	{ id: "13", name: "Goût du challenge" },
	{ id: "14", name: "Leadership" },
	{ id: "15", name: "Réflexion" },
	{ id: "16", name: "Altruisme" },
	{ id: "17", name: "Sens du service" },
]

function PoleAtouts() {
	const [selectedAtouts, setSelectedAtouts] = useState<string[]>([])
	const [ratings, setRatings] = useState<Record<string, number>>({})

	const [error, setError] = useState<string | null>(null)
	const location = useLocation()
	const state = location.state as LocationState
	const [userId, setUserId] = useState<number | null>(null)
	const [cvSkillId, setCvSkillId] = useState<number | null>(null)
	const { editMode } = state
	const navigate = useNavigate()

	// Effet pour charger les données initiales
	useEffect(() => {
		const fetchData = async () => {
			try {
				const storedUserId = localStorage.getItem("selectedUserId")
				const storedCvSkillId =
					localStorage.getItem("selectedCvSkillId")

				if (!storedUserId)
					throw new Error("ID utilisateur sélectionné non trouvé")
				setUserId(Number(storedUserId))

				if (storedCvSkillId) setCvSkillId(Number(storedCvSkillId))

				if (editMode && storedCvSkillId) {
					const cvSkill = await cvskillService.getCvSkillById(
						Number(storedCvSkillId),
					)
					if (cvSkill?.poleAtouts) {
						setSelectedAtouts(
							cvSkill.poleAtouts.map(atout => atout.atout),
						)
						const initialRatings: Record<string, number> = {}
						cvSkill.poleAtouts.forEach((atout: any) => {
							initialRatings[atout.atout] = atout.note
						})
						setRatings(initialRatings)
					}
				} else {
					// ✅ CHARGER DEPUIS LOCALSTORAGE EN MODE CRÉATION
					const storedAtouts = localStorage.getItem(
						`selectedAtouts-${storedUserId}`,
					)
					if (storedAtouts) {
						const parsedAtouts = JSON.parse(storedAtouts)
						console.log(
							"[PoleAtouts] 🔍 Loaded from localStorage:",
							parsedAtouts,
						)

						// ✅ NORMALISER : Extraire les noms des atouts
						const atoutNames: string[] = []
						const atoutRatings: Record<string, number> = {}

						parsedAtouts.forEach((item: any) => {
							let atoutName: string
							let note = 0

							// Si c'est { atout: "...", note: ... }
							if (
								item &&
								typeof item === "object" &&
								typeof item.atout === "string"
							) {
								atoutName = item.atout
								note = item.note || 0
							}
							// Si c'est { atout: { name: "..." }, note: ... }
							else if (
								item &&
								typeof item === "object" &&
								item.atout?.name
							) {
								atoutName = item.atout.name
								note = item.note || 0
							}
							// Si c'est juste un string
							else if (typeof item === "string") {
								atoutName = item
							}
							// Si c'est { id: "...", name: "..." }
							else if (
								item &&
								typeof item === "object" &&
								item.name
							) {
								atoutName = item.name
							} else {
								console.warn(
									"[PoleAtouts] ⚠️ Format inattendu:",
									item,
								)
								return
							}

							atoutNames.push(atoutName)
							atoutRatings[atoutName] = note
						})

						console.log(
							"[PoleAtouts] ✅ Normalized names:",
							atoutNames,
						)
						console.log(
							"[PoleAtouts] ✅ Normalized ratings:",
							atoutRatings,
						)

						setSelectedAtouts(atoutNames)
						setRatings(atoutRatings)
					}
				}
			} catch (error) {
				console.error("[PoleAtouts] ❌ Error loading data:", error)
				setError(
					"Impossible de récupérer les données. Veuillez réessayer.",
				)
			}
		}

		fetchData()
	}, [editMode])

	// Gestion du changement de sélection des atouts
	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, checked } = event.target
		let updatedAtouts: string[]

		if (checked) {
			if (selectedAtouts.length >= 5) {
				setError("Vous ne pouvez sélectionner que 5 choix maximum.")
				return
			}
			updatedAtouts = [...selectedAtouts, name]
		} else {
			updatedAtouts = selectedAtouts.filter(item => item !== name)
		}

		setSelectedAtouts(updatedAtouts)
		setError(null)

		if (!editMode && userId) {
			// ✅ TOUJOURS SAUVEGARDER EN FORMAT { atout: STRING, note: NUMBER }
			const atoutsWithNotes = updatedAtouts.map(atoutName => ({
				atout: atoutName, // ✅ STRING, pas d'objet !
				note: ratings[atoutName] ?? 0,
			}))

			console.log(
				"[PoleAtouts] 💾 Saving to localStorage:",
				atoutsWithNotes,
			)

			localStorage.setItem(
				`selectedAtouts-${userId}`,
				JSON.stringify(atoutsWithNotes),
			)
		}
	}

	// Handle rating selection for each atout
	const handleRatingChange = (
		event: React.ChangeEvent<HTMLSelectElement>,
		atoutName: string, // ✅ C'est une STRING
	) => {
		const { value } = event.target
		const newRatings = {
			...ratings,
			[atoutName]: parseInt(value, 10),
		}
		setRatings(newRatings)

		// ✅ Mettre à jour le localStorage immédiatement avec les nouvelles notes
		if (!editMode && userId) {
			const atoutsWithNotes = selectedAtouts.map(name => ({
				atout: name, // ✅ STRING
				note: newRatings[name] ?? 0,
			}))

			console.log(
				"[PoleAtouts] 💾 Updating notes in localStorage:",
				atoutsWithNotes,
			)

			localStorage.setItem(
				`selectedAtouts-${userId}`,
				JSON.stringify(atoutsWithNotes),
			)
		}
	}

	// Mutation pour mettre à jour le CV Skill
	const updateCvSkillMutation = useMutation(
		(updatedCvSkill: any) => {
			if (!cvSkillId || !userId)
				throw new Error("CV Skill ID ou User ID invalide")
			return cvskillService.updateCvSkill(
				cvSkillId,
				updatedCvSkill,
				userId,
			)
		},
		{
			onSuccess: () => {
				Swal.fire({
					icon: "success",
					title: "Atouts mis à jour avec succès",
					showConfirmButton: false,
					timer: 1500,
				})
				navigate("/cvskill/cv", {
					state: { userId, cvSkillId },
				})
			},
			onError: () => {
				setError("Erreur lors de la mise à jour. Veuillez réessayer.")
			},
		},
	)

	// Handle form submission
	const handleSubmit = async () => {
		if (!userId) {
			setError("ID utilisateur manquant. Impossible de continuer.")
			return
		}

		// ✅ TOUJOURS CRÉER UN TABLEAU AVEC { atout: STRING, note: NUMBER }
		const atoutsData = selectedAtouts.map(atoutName => ({
			atout: atoutName, // ✅ STRING !
			note: ratings[atoutName] || 0,
		}))

		console.log("[PoleAtouts] 📤 Submitting atouts:", atoutsData)

		if (editMode && cvSkillId) {
			try {
				const currentCvSkill =
					await cvskillService.getCvSkillById(cvSkillId)
				const updatedCvSkill = {
					...currentCvSkill,
					poleAtouts: atoutsData,
				}
				updateCvSkillMutation.mutate(updatedCvSkill)
			} catch {
				setError("Erreur lors de la mise à jour. Veuillez réessayer.")
			}
		} else {
			localStorage.setItem(
				`selectedAtouts-${userId}`,
				JSON.stringify(atoutsData),
			)
			navigate("/cvskill/poleInterets", {
				state: { ...state, userId, cvSkillId, poleAtouts: atoutsData },
			})
		}
	}

	const selectedCount = selectedAtouts.length

	return (
		<div className="container mx-auto p-4 max-w-4xl relative overflow-y-auto h-[calc(100vh-8rem)] pb-16">
			<style>{`
                .container::-webkit-scrollbar {
                    display: none;
                }
                .container {
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                }
                
                .page-header {
                    background: linear-gradient(135deg, #2daf8e 0%, #238f74 100%);
                    color: white;
                    padding: 20px 24px;
                    border-radius: 16px;
                    text-align: center;
                    box-shadow: 0 4px 12px rgba(45, 175, 142, 0.3);
                    margin-bottom: 24px;
                }
                
                .page-header h2 {
                    font-size: 24px;
                    font-weight: 700;
                    margin: 0;
                }
                
                .atout-card {
                    background: white;
                    border: 2px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 16px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                }
                
                .atout-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(45, 175, 142, 0.2);
                    border-color: #7dd4c1;
                }
                
                .atout-card.selected {
                    background: linear-gradient(135deg, #d4f5ed 0%, #b8ede0 100%);
                    border-color: #2daf8e;
                    box-shadow: 0 4px 12px rgba(45, 175, 142, 0.3);
                }
                
                .selection-checkbox {
                    appearance: none;
                    width: 24px;
                    height: 24px;
                    border: 2px solid #d1d5db;
                    border-radius: 6px;
                    cursor: pointer;
                    position: relative;
                    transition: all 0.2s ease;
                    flex-shrink: 0;
                }
                
                .selection-checkbox:hover {
                    border-color: #2daf8e;
                }
                
                .selection-checkbox:checked {
                    background-color: #2daf8e;
                    border-color: #2daf8e;
                }
                
                .selection-checkbox:checked::after {
                    content: "✓";
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    color: white;
                    font-size: 16px;
                    font-weight: bold;
                }
                
                .rating-select {
                    background: white;
                    border: 2px solid #2daf8e;
                    border-radius: 8px;
                    padding: 6px 10px;
                    font-size: 14px;
                    font-weight: 600;
                    color: #2daf8e;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    min-width: 60px;
                }
                
                .rating-select:hover {
                    background: #d4f5ed;
                }
                
                .rating-select:focus {
                    outline: none;
                    box-shadow: 0 0 0 3px rgba(45, 175, 142, 0.2);
                }
                
                .counter-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background: ${selectedCount >= 3 && selectedCount <= 5 ? "#2daf8e" : "#f3f4f6"};
                    color: ${selectedCount >= 3 && selectedCount <= 5 ? "white" : "#6b7280"};
                    padding: 10px 20px;
                    border-radius: 20px;
                    font-weight: 600;
                    font-size: 14px;
                    margin: 16px auto;
                    width: fit-content;
                }
                
                .action-button {
                    background: linear-gradient(135deg, #2daf8e 0%, #238f74 100%) !important;
                    box-shadow: 0 4px 12px rgba(45, 175, 142, 0.3) !important;
                    text-transform: none !important;
                    font-size: 16px !important;
                    font-weight: 600 !important;
                    padding: 12px 24px !important;
                    border-radius: 12px !important;
                    width: 100%;
                    transition: all 0.3s ease !important;
                }
                
                .action-button:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(45, 175, 142, 0.4) !important;
                }
                
                .action-button:disabled {
                    background: #d1d5db !important;
                    box-shadow: none !important;
                    cursor: not-allowed;
                }
            `}</style>

			<div className="page-header">
				<h2>{editMode ? "Modifier vos atouts" : "Pôle Atouts"}</h2>
			</div>

			{error && (
				<p className="text-red-500 text-center bg-red-50 p-3 rounded-lg mb-4">
					⚠️ {error}
				</p>
			)}

			<div className="counter-badge">
				{selectedCount >= 3 && selectedCount <= 5 ? "✓" : "📊"}{" "}
				{selectedCount} / 5 atouts sélectionnés
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
				{atouts.map(atout => {
					const isSelected = selectedAtouts.includes(atout.name)
					return (
						<div
							key={atout.id}
							className={`atout-card ${isSelected ? "selected" : ""}`}
						>
							<div className="flex items-center justify-between gap-3">
								<div className="flex items-center gap-3 flex-1">
									<input
										type="checkbox"
										id={atout.id}
										name={atout.name}
										checked={isSelected}
										onChange={handleChange}
										className="selection-checkbox"
									/>

									<label
										htmlFor={atout.id}
										className="text-gray-800 font-medium cursor-pointer flex-1"
									>
										{atout.name}
									</label>
								</div>

								{isSelected && (
									<select
										className="rating-select"
										value={ratings[atout.name] || 0}
										onChange={e =>
											handleRatingChange(e, atout.name)
										}
									>
										{Array.from({ length: 11 }, (_, i) => (
											<option key={i} value={i}>
												{i}/10
											</option>
										))}
									</select>
								)}
							</div>
						</div>
					)
				})}
			</div>

			<p className="mt-6 font-bold text-center text-red-500 bg-red-50 p-3 rounded-lg">
				⚠️ Sélectionnez entre 3 et 5 atouts
			</p>

			<div className="flex justify-center mt-8">
				<Button
					onClick={handleSubmit}
					variant="contained"
					className="action-button"
					disabled={selectedCount < 3 || selectedCount > 5}
				>
					{editMode
						? "Mettre à jour les atouts"
						: "Sauvegarder les choix →"}
				</Button>
			</div>
		</div>
	)
}

export default PoleAtouts
